import { useEffect, useState } from "react";
import { PageSkeleton } from "@/components/PageSkeleton";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users2, UserPlus, Repeat, HandCoins, Search, Pencil, Trash2 } from "lucide-react";

const Stat = ({ label, value, icon: Icon, accent, testid }) => (
  <Card data-testid={testid} className="p-4">
    <div className="flex items-center gap-2.5">
      <div className={`grid place-items-center h-9 w-9 rounded-lg shrink-0 ${accent.bg}`}><Icon className={`h-4 w-4 ${accent.text}`} /></div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">{label}</p>
        <p className="font-heading text-base sm:text-lg font-extrabold mt-0.5 truncate">{value}</p>
      </div>
    </div>
  </Card>
);

export default function Customers() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [editFor, setEditFor] = useState(null);
  const [eform, setEform] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/customers/overview").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    setBusy(true);
    try {
      await api.post("/customers", { name: form.name.trim(), phone: form.phone.trim() });
      toast.success("Pelanggan berhasil ditambahkan");
      setOpen(false); setForm({ name: "", phone: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = (c) => { setEditFor(c); setEform({ name: c.name, phone: c.phone || "" }); };

  const submitEdit = async () => {
    if (!eform.name.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    setBusy(true);
    try {
      await api.patch(`/customers/${editFor.id}`, { name: eform.name.trim(), phone: eform.phone.trim() });
      toast.success("Pelanggan diperbarui");
      setEditFor(null); setEform(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus pelanggan "${c.name}"? Riwayat order tetap tersimpan.`)) return;
    try {
      const res = await api.post("/deletion-requests", { resource_type: "customer", resource_id: c.id, label: c.name });
      toast.success(res.data.pending ? "Permintaan hapus dikirim, menunggu persetujuan Owner" : "Pelanggan dihapus");
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <PageSkeleton stats={4} rows={6} />;

  const rows = data.customers.filter((c) =>
    c.name.toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Data Pelanggan</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Pelanggan baru, langganan, dan total piutang.</p>
        </div>
        <Button data-testid="cust-add-btn" size="sm" className="gap-1.5 h-8 text-xs shrink-0" onClick={() => setOpen(true)}>
          <UserPlus className="h-3.5 w-3.5" /> Tambah Pelanggan
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="cust-total" label="Total Pelanggan" value={data.total_customers} icon={Users2}
          accent={{ bg: "bg-indigo-100 dark:bg-indigo-950", text: "text-indigo-600" }} />
        <Stat testid="cust-new" label="Pelanggan Baru (bln ini)" value={data.new_customers} icon={UserPlus}
          accent={{ bg: "bg-emerald-100 dark:bg-emerald-950", text: "text-emerald-600" }} />
        <Stat testid="cust-returning" label="Pelanggan Lama" value={data.returning_customers} icon={Repeat}
          accent={{ bg: "bg-sky-100 dark:bg-sky-950", text: "text-sky-600" }} />
        <Stat testid="cust-piutang" label="Total Piutang" value={formatIDR(data.total_piutang)} icon={HandCoins}
          accent={{ bg: "bg-rose-100 dark:bg-rose-950", text: "text-rose-600" }} />
      </div>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="cust-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / no. telepon" className="pl-9" />
          </div>
        </div>
        <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>No. Telepon</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Jml Order</TableHead>
                <TableHead className="text-right">Total Belanja</TableHead>
                <TableHead className="text-right">Piutang</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Belum ada pelanggan.</TableCell></TableRow>}
              {rows.map((c) => (
                <TableRow key={c.id} data-testid={`cust-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.phone || "-"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={c.is_new
                      ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300"
                      : "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950 dark:text-sky-300"}>
                      {c.is_new ? "Baru" : "Lama"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{c.order_count}</TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap">{formatIDR(c.total_spent)}</TableCell>
                  <TableCell className={`text-right font-semibold whitespace-nowrap ${c.piutang > 0 ? "text-rose-600" : "text-muted-foreground"}`}>{formatIDR(c.piutang)}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-0.5 justify-end">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEdit(c)} data-testid={`cust-edit-${c.id}`} title="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {c.pending_deletion ? (
                        <span data-testid={`cust-pending-${c.id}`} className="text-[9px] font-semibold text-amber-600 px-1.5">Menunggu persetujuan</span>
                      ) : (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => remove(c)} data-testid={`cust-delete-${c.id}`} title="Hapus">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="lg:hidden divide-y divide-border">
          {rows.length === 0 && <p className="text-center text-muted-foreground text-xs py-8">Belum ada pelanggan.</p>}
          {rows.map((c) => (
            <div key={c.id} data-testid={`cust-row-mobile-${c.id}`} className="p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">{c.name}
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${c.is_new ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300" : "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950 dark:text-sky-300"}`}>{c.is_new ? "Baru" : "Lama"}</Badge>
                  </p>
                  <p className="text-[11px] text-muted-foreground">{c.phone || "-"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-muted-foreground">Total Belanja</p>
                  <p className="text-sm font-bold whitespace-nowrap">{formatIDR(c.total_spent)}</p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="text-muted-foreground">{c.order_count} order</span>
                <span className={c.piutang > 0 ? "text-rose-600 font-semibold" : "text-muted-foreground"}>Piutang: {formatIDR(c.piutang)}</span>
              </div>
              <div className="flex justify-end gap-1 pt-0.5">
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1 px-2" onClick={() => openEdit(c)} data-testid={`cust-edit-mobile-${c.id}`}>
                  <Pencil className="h-3 w-3" /> Edit
                </Button>
                {c.pending_deletion ? (
                  <span data-testid={`cust-pending-mobile-${c.id}`} className="text-[10px] font-semibold text-amber-600 self-center px-2">Menunggu persetujuan hapus</span>
                ) : (
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 px-2 text-rose-600 hover:text-rose-700" onClick={() => remove(c)} data-testid={`cust-delete-mobile-${c.id}`}>
                    <Trash2 className="h-3 w-3" /> Hapus
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm({ name: "", phone: "" }); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Tambah Pelanggan</DialogTitle>
            <DialogDescription className="text-xs">Tambahkan pelanggan baru ke daftar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1"><Label className="text-xs">Nama Pelanggan</Label><Input data-testid="cust-form-name" className="h-9 text-xs" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">No. Telepon</Label><Input data-testid="cust-form-phone" className="h-9 text-xs" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xx..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={busy} data-testid="cust-form-submit" variant="gold" size="sm" className="h-8 text-xs">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Edit Pelanggan {editFor ? `— ${editFor.name}` : ""}</DialogTitle>
            <DialogDescription className="text-xs">Perubahan nama ikut memperbarui riwayat order pelanggan.</DialogDescription>
          </DialogHeader>
          {editFor && eform && (
            <div className="space-y-3 py-1">
              <div className="space-y-1"><Label className="text-xs">Nama Pelanggan</Label><Input data-testid="cust-edit-name" className="h-9 text-xs" value={eform.name} onChange={(e) => setEform({ ...eform, name: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">No. Telepon</Label><Input data-testid="cust-edit-phone" className="h-9 text-xs" value={eform.phone} onChange={(e) => setEform({ ...eform, phone: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditFor(null)}>Batal</Button>
            <Button onClick={submitEdit} disabled={busy} data-testid="cust-edit-submit" variant="gold" size="sm" className="h-8 text-xs">Simpan Perubahan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
